/**
 * patient-portal view helpers — pure mappers from wire fields to the design-system vocabulary.
 * Clinical flags and consent verdicts ALWAYS resolve to a color + glyph + text triple (never color
 * alone — daltonisme + médico-légal).
 */
import type { BadgeTone } from "@/ui";

/** A chip descriptor: the UI Badge tone + the i18n key for its text half. */
export interface ChipVisual {
  tone: BadgeTone;
  labelKey: string;
}

/**
 * Map a CompteRenduLigne `drapeau` (the frozen clinical flag) onto a clinical Badge tone. The Badge
 * injects the mandated glyph for clinical tones automatically; unknown/empty flags read as neutral.
 * Accepts the common server spellings (N/H/B/C and their long forms) defensively.
 */
export function flagVisual(drapeau: string | null | undefined): ChipVisual {
  const f = (drapeau ?? "").trim().toLowerCase();
  switch (f) {
    case "n":
    case "normal":
      return { tone: "normal", labelKey: "pp.flag.normal" };
    case "b":
    case "bas":
    case "low":
      return { tone: "low", labelKey: "pp.flag.bas" };
    case "h":
    case "haut":
    case "high":
      return { tone: "high", labelKey: "pp.flag.haut" };
    case "c":
    case "critique":
    case "critical":
    case "panique":
      return { tone: "critical", labelKey: "pp.flag.critique" };
    default:
      return { tone: "neutral", labelKey: "pp.flag.inconnu" };
  }
}

/** Map the live publish-gate boolean onto the consent/publish verdict chip. */
export function consentVisual(canPublish: boolean): ChipVisual {
  return canPublish
    ? { tone: "success", labelKey: "pp.consent.accorde" }
    : { tone: "danger", labelKey: "pp.consent.revoque" };
}
